import { useState, useCallback } from 'react';
import axios from 'axios';
import { getToken } from '../../services/authToken';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * useInterviewSimSubmit — POST /api/cabin/interview-sim/{id}/submit
 * Multipart form upload of video blob
 * Returns { status: 'pending_review' }
 */
export function useInterviewSimSubmit() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [progress, setProgress] = useState(0);

  const submit = useCallback(async ({ sessionId, videoBlob, mimeType = 'video/webm' } = {}) => {
    setLoading(true);
    setError(null);
    setProgress(0);

    if (!sessionId || !videoBlob) {
      setError('session_id and videoBlob are required');
      setLoading(false);
      return null;
    }

    // Mock session IDs don't hit the backend
    if (String(sessionId).startsWith('mock-')) {
      await new Promise(resolve => setTimeout(resolve, 1200));
      setProgress(100);
      setLoading(false);
      return { status: 'pending_review' };
    }

    try {
      const token = getToken();
      const ext = mimeType.includes('mp4') ? 'mp4' : 'webm';
      const formData = new FormData();
      formData.append('video', videoBlob, `interview-${sessionId}.${ext}`);

      const res = await axios.post(
        `${API}/cabin/interview-sim/${sessionId}/submit`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data',
          },
          onUploadProgress: (e) => {
            if (e.total) {
              setProgress(Math.round((e.loaded / e.total) * 100));
            }
          },
        }
      );
      return res.data; // { status: 'pending_review' }
    } catch (err) {
      // Mock fallback
      if (err?.response?.status !== 400 && err?.response?.status !== 401 && err?.response?.status !== 403) {
        setProgress(100);
        return { status: 'pending_review' };
      }
      const msg = err?.response?.data?.message_tr || err?.response?.data?.detail || err.message;
      setError(msg);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  return { loading, error, progress, submit };
}
